import type { GetTimeSettingsCustomTimeRangeCategoriesQuery } from 'generated/resources/graphql'
import orderBy from 'lodash/orderBy'

type TimeSettingsCustomTimeRangeCategory =
  GetTimeSettingsCustomTimeRangeCategoriesQuery['timeSettings']['customTimeRangeCategoriesConnection']['nodes'][number]

class TimeCategoriesUtils {
  static flexibleCategories<T extends Pick<TimeSettingsCustomTimeRangeCategory, 'timeType'>>(
    categories: T[]
  ): T[] {
    return categories.filter((c) => this.isFlexible(c))
  }

  static specialHoursCategories<
    T extends Pick<TimeSettingsCustomTimeRangeCategory, 'categoryType'>,
  >(categories: T[]): T[] {
    return categories.filter((c) => this.isSpecialHourCategory(c))
  }

  static extraHoursCategories<T extends Pick<TimeSettingsCustomTimeRangeCategory, 'categoryType'>>(
    categories: T[]
  ): T[] {
    return categories.filter((c) => this.isExtraHourCategory(c))
  }

  static activeCategories<T extends Pick<TimeSettingsCustomTimeRangeCategory, 'archived'>>(
    categories: T[]
  ): T[] {
    return categories.filter((c) => this.isActive(c))
  }

  static orderByName<T extends Pick<TimeSettingsCustomTimeRangeCategory, 'name'>>(
    categories: T[]
  ): T[] {
    return orderBy(categories, (category) => category.name, 'asc')
  }

  static hasSpecialHoursCategories<
    T extends Pick<TimeSettingsCustomTimeRangeCategory, 'categoryType'>,
  >(categories: T[]): boolean {
    return this.specialHoursCategories(categories).length > 0
  }

  static hasExtraHoursCategories<
    T extends Pick<TimeSettingsCustomTimeRangeCategory, 'categoryType'>,
  >(categories: T[]): boolean {
    return this.extraHoursCategories(categories).length > 0
  }

  static hasCategories<T extends Pick<TimeSettingsCustomTimeRangeCategory, 'categoryType'>>(
    categories: T[]
  ): boolean {
    return categories.length > 0
  }

  static isFlexible(category: Pick<TimeSettingsCustomTimeRangeCategory, 'timeType'>) {
    return category.timeType === 'any_time'
  }

  static isSpecialHourCategory(
    category: Pick<TimeSettingsCustomTimeRangeCategory, 'categoryType'>
  ) {
    return category.categoryType === 'special_hour'
  }

  static isExtraHourCategory(category: Pick<TimeSettingsCustomTimeRangeCategory, 'categoryType'>) {
    return category.categoryType === 'extra_hour'
  }

  static isActive(category: Pick<TimeSettingsCustomTimeRangeCategory, 'archived'>) {
    return !category.archived
  }

  static findCategoryById<T extends Pick<TimeSettingsCustomTimeRangeCategory, 'id'>>(
    categories: T[],
    id: string
  ): T | undefined {
    return categories.find((c) => c.id === id)
  }

  static nonDefaultCategories<T extends Pick<TimeSettingsCustomTimeRangeCategory, 'default'>>(
    categories: T[]
  ): T[] {
    return categories.filter((c) => !c.default)
  }
}

export default TimeCategoriesUtils
